import React, { useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import props from '../../../../redux/props'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartOptions } from 'chart.js'
import { Bar as BarChart, getElementAtEvent } from 'react-chartjs-2'
import { formatNumber } from '../../../../utility'
import { isNumber, round } from 'lodash'
import Button from '../../Button/Button'
import getBarTotalPlugin from '../getBarTotalPlugin'
import { colors, colorsTransparent } from './colors'
import annotationPlugin from 'chartjs-plugin-annotation'

const formatLabel = (str: string, maxwidth: number): string | string[] => {
  var sections = []
  var words = str.split(' ')
  var temp = ''

  words.forEach(function (item, index) {
    if (temp.length > 0) {
      var concat = temp + ' ' + item

      if (concat.length > maxwidth) {
        sections.push(temp)
        temp = ''
      } else {
        if (index === words.length - 1) {
          sections.push(concat)
          return
        } else {
          temp = concat
          return
        }
      }
    }

    if (index === words.length - 1) {
      sections.push(item)
      return
    }

    if (item.length < maxwidth) {
      temp = item
    } else {
      sections.push(item)
    }
  })

  return sections
}

// 1) HORIZONTAL LINE PLUGIN (no changes needed; it will still work)
const horizontalLinePlugin = {
  id: 'horizontalLinePlugin',
  afterDraw: (chart) => {
    const {
      ctx,
      chartArea: { left, right },
      scales: { y },
      config,
    } = chart

    const { redLineValue } = config.options.plugins.horizontalLinePlugin || {}
    if (typeof redLineValue !== 'number') return

    const yPixel = y.getPixelForValue(redLineValue)

    ctx.save()

    // Draw the line
    ctx.strokeStyle = '#FF0000'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(left, yPixel)
    ctx.lineTo(right, yPixel)
    ctx.stroke()

    // Add the value text
    ctx.fillStyle = '#FF0000'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'bottom'
    ctx.font = '12px Arial'
    ctx.fillText(`${formatNumber(redLineValue)} m²`, left + 5, yPixel)

    ctx.restore()
  },
}

// Configure annotations instead of using a custom plugin
const getAnnotations = (datasets, percentageWidth, suffix) => {
  const annotations = {}

  datasets.forEach((dataset, datasetIndex) => {
    if (dataset.limits !== undefined && Array.isArray(dataset.limits)) {
      dataset.limits.forEach((limit, index) => {
        if (limit !== undefined) {
          annotations[`limit-${datasetIndex}-${index}`] = {
            type: 'line',
            xMin: index - 0.4 * (percentageWidth || 1),
            xMax: index + 0.4 * (percentageWidth || 1),
            yMin: limit,
            yMax: limit,
            borderColor: '#FF0000',
            borderWidth: 1,
            // drawTime: 'beforeDatasetsDraw', // Draw behind the bars
            // label: {
            //   display: true,
            //   content: `${formatNumber(limit)}${suffix} (${formatNumber(round(diff, 2))}${suffix})`,
            //   position: 'center',
            //   color: '#033047',
            //   backgroundColor: 'transparent',
            //   font: {
            //     size: 10,
            //   },
            //   yAdjust: -5,
            // },
          }
        }
      })
    }
  })

  return annotations
}

// 3) REGISTER CHART ELEMENTS & PLUGINS
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, horizontalLinePlugin, annotationPlugin)

export type GraphValue = {
  label: string
  value: number
}

export type Item = {
  key: string
  label: string
  limit?: number
  values: GraphValue[]
  isActive?: boolean
}

type Props = {
  items: Item[]
  suffix?: string
  showGrid?: boolean
  onClickCallback?: (itemKey: string) => void
  useTransparentColors?: boolean
  redLineValue?: number
  enableDownload?: boolean
  percentageWidth?: number
  showLegend?: boolean
  enableRound?: boolean
}

const BarStacked: React.FC<Props> = ({
  items,
  suffix,
  showGrid = false,
  onClickCallback,
  useTransparentColors = true,
  redLineValue,
  enableDownload = false,
  percentageWidth,
  showLegend = false,
  enableRound = true,
}) => {
  const t = useSelector((s) => s[props.TRANSLATION])

  // Use a ref for the Chart.js instance
  const chartRef = useRef<any>(null)

  // Holds the Base64-encoded PNG image of the chart
  const [image, setImage] = useState<string | null>(null)

  // Prepare the data for the stacked bar chart
  const data = {
    labels: items.map((i) => i.label),
    datasets: items[0].values.map((v, i) => ({
      data: items.map((item) => item.values[i].value),
      limits: items.map((item) => item.limit),
      backgroundColor: items.map((item) =>
        item.isActive || !useTransparentColors ? colors[i].backgroundColor : colorsTransparent[i].backgroundColor,
      ),
      borderColor: items.map((item) => (item.isActive || !useTransparentColors ? colors[i].borderColor : colorsTransparent[i].borderColor)),
      label: v.label,
    })),
  }

  // Chart options
  const options = {
    layout: {
      padding: {
        top: 20,
      },
      autoPadding: true,
    },
    plugins: {
      legend: showLegend
        ? {
            display: true,
            position: 'bottom',
            align: 'start',
            labels: {
              fullSize: false,
              usePointStyle: false,
              pointStyle: 'rectRounded',
              padding: 20,
              boxWidth: 10,
              boxHeight: 10,
              font: {
                size: 12,
              },
            },
          }
        : { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            // 'context.parsed' => { x: number, y: number }
            const datasetLabel = items[context.dataIndex].values[context.datasetIndex].label

            if (context.parsed) {
              const value = context.parsed.y
              return `${datasetLabel}: ${formatNumber(enableRound ? round(value, 2) : value)}${suffix || t.generic.percent}`
            }
            return context.label || ''
          },
        },
      },
      horizontalLinePlugin: {
        // Pass redLineValue to the plugin
        redLineValue,
      },
      annotation: {
        drawTime: 'beforeDraw',
        annotations: getAnnotations(data.datasets, percentageWidth, suffix),
      },
    },
    responsive: true,
    barPercentage: percentageWidth || 1,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: showGrid,
        },
        ticks: {
          color: 'black', // Set the x-axis labels color to black
          callback: function (index) {
            const label = items[index].label
            return formatLabel(label, 45)
          },
        },
      },
      y: {
        stacked: true,
        grid: {
          display: showGrid,
        },
        ticks: {
          color: 'black', // Set the x-axis labels color to black
        },
        // Add padding to the y-axis scale to prevent values from being at the top
        // suggestedMax: (context) => {
        //   const maxValue = context.chart.data.datasets.reduce((max, dataset) => {
        //     const datasetMax = Math.max(...dataset.data)
        //     return datasetMax > max ? datasetMax : max
        //   }, 0)
        //   return maxValue * 1.5 // Add 10% padding to the top
        // },
      },
    },
    // Use the animation callback to capture the chart image after render
    animation: {
      onComplete: () => {
        if (chartRef.current && enableDownload) {
          const chart = chartRef.current
          const canvas = chart.canvas
          setImage(canvas.toDataURL())
        }
      },
    },
  } as ChartOptions<'bar'>

  // Handle clicks on the chart
  const onClick = (event) => {
    if (onClickCallback) {
      const interactionItem = getElementAtEvent(chartRef.current, event)
      if (Array.isArray(interactionItem) && interactionItem.length) {
        const itemIndex = interactionItem[0].index
        onClickCallback(items[itemIndex].key)
      }
    }
  }

  const download = () => {
    if (!image) return
    const a = document.createElement('a')
    a.href = image
    a.download = 'chart.png'
    a.click()
  }

  return (
    <div className="w-full">
      {image && enableDownload && <Button text={t.generic.download} onClick={download} small className="ml-auto mt-5" />}
      <BarChart
        ref={chartRef}
        options={options}
        data={data}
        onClick={onClick}
        plugins={[getBarTotalPlugin(suffix || t.generic.squareMeterSuffix, true, isNumber(items[0].limit) && items[0].limit > 0, enableRound)]}
      />
    </div>
  )
}

export default BarStacked
